import React, { useEffect, useRef } from 'react';

/**
 * Razorpay Subscription Button embed.
 *
 * Same idea as ``RazorpayPaymentButton`` — a hosted, pre-configured
 * checkout widget — but this one uses Razorpay's *subscription* widget
 * script (``cdn.razorpay.com/static/widget/subscription-button.js``)
 * which reads the button config via ``data-subscription_button_id``
 * instead of ``data-payment_button_id``.
 *
 * Used for recurring / subscription plans (e.g. the USD monthly
 * "Executive Thought Leadership" button) where Razorpay handles the
 * mandate authorisation + subscription creation entirely inside their
 * iframe, and our webhook auto-provisions access by email.
 *
 * Docs: https://razorpay.com/docs/payments/subscriptions/subscription-buttons/
 */
export function RazorpaySubscriptionButton({
  buttonId,
  className = '',
  testId,
  buttonTheme = 'brand-color',
}) {
  const formRef = useRef(null);

  useEffect(() => {
    const form = formRef.current;
    if (!form || !buttonId) return undefined;

    // Defensive shim — see RazorpayPaymentButton for the rationale.
    if (typeof window !== 'undefined' && typeof window.errorQueue === 'undefined') {
      window.errorQueue = [];
    }

    // Guard against React 18 StrictMode's double-invoke of effects and
    // any parent re-mounts that would inject duplicate scripts.
    if (form.querySelector('script[data-subscription_button_id]')) return undefined;

    const s = document.createElement('script');
    s.src = 'https://cdn.razorpay.com/static/widget/subscription-button.js';
    s.async = true;
    s.setAttribute('data-subscription_button_id', buttonId);
    if (buttonTheme) s.setAttribute('data-button_theme', buttonTheme);
    form.appendChild(s);

    // NOTE: We intentionally do *not* remove the script on unmount — see
    // ``RazorpayPaymentButton`` for the full rationale (Razorpay's widget
    // holds internal references to its own <script> tag; removing it
    // while the runtime is still live crashes the widget).
    return undefined;
  }, [buttonId, buttonTheme]);

  return (
    <form
      ref={formRef}
      className={className}
      data-testid={testId || `razorpay-subscription-button-${buttonId}`}
    />
  );
}

export default RazorpaySubscriptionButton;
